import { useEffect, useMemo, useState } from 'react';
import { Users, Plus, Lock, Unlock, AlertTriangle } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';

interface Customer {
  id: string;
  business_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  kind: string;
  credit_limit: number;
  payment_terms_days: number;
  on_hold: boolean;
  hold_reason: string | null;
}

interface Invoice {
  id: string;
  business_id: string;
  invoice_number: string;
  customer_id: string | null;
  customer_name: string;
  issue_date: string;
  due_date: string | null;
  total: number;
  paid_amount: number;
  status: string;
}

interface AgingRow {
  customer: Customer;
  outstanding: number;
  buckets: { d0_30: number; d31_60: number; d61_90: number; d91plus: number };
  oldestInvoice: Invoice | null;
}

function daysBetween(from: string, to: Date): number {
  const f = new Date(from);
  return Math.floor((to.getTime() - f.getTime()) / 86_400_000);
}

export default function Customers() {
  const business = useAuthStore((s) => s.business)!;
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tab, setTab] = useState<'list' | 'aging'>('aging');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    credit_limit: 0,
    terms: 30,
    kind: 'individual',
  });

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const [{ data: c }, { data: i }] = await Promise.all([
      supabase.from('customers').select('*').eq('business_id', business.id).order('name'),
      supabase.from('invoices').select('*').eq('business_id', business.id),
    ]);
    setCustomers((c ?? []) as Customer[]);
    setInvoices((i ?? []) as Invoice[]);
  }

  async function create() {
    if (!form.name) {
      toast.show('Customer name required', { tone: 'warning' });
      return;
    }
    await supabase.from('customers').insert({
      business_id: business.id,
      name: form.name,
      email: form.email || null,
      phone: form.phone || null,
      address: null,
      kind: form.kind,
      credit_limit: form.credit_limit,
      payment_terms_days: form.terms,
      on_hold: false,
      hold_reason: null,
      notes: null,
    });
    toast.show('Customer added.', { tone: 'success' });
    setOpen(false);
    setForm({ name: '', email: '', phone: '', credit_limit: 0, terms: 30, kind: 'individual' });
    void load();
  }

  async function toggleHold(c: Customer, reason?: string) {
    await supabase
      .from('customers')
      .update({
        on_hold: !c.on_hold,
        hold_reason: !c.on_hold ? (reason ?? holdReason ?? '90+ days overdue') : null,
      })
      .eq('id', c.id);
    setSelected(null);
    setHoldReason('');
    toast.show(c.on_hold ? `${c.name} taken off hold` : `${c.name} placed on credit hold`, {
      tone: c.on_hold ? 'success' : 'warning',
    });
    void load();
  }

  // Aging report
  const aging: AgingRow[] = useMemo(() => {
    const now = new Date();
    const rows: AgingRow[] = customers.map((c) => {
      const myInvoices = invoices.filter(
        (i) => i.customer_id === c.id && i.status !== 'paid' && i.status !== 'cancelled',
      );
      const buckets = { d0_30: 0, d31_60: 0, d61_90: 0, d91plus: 0 };
      let oldest: Invoice | null = null;
      for (const inv of myInvoices) {
        const owed = inv.total - inv.paid_amount;
        if (owed <= 0) continue;
        const days = inv.due_date ? daysBetween(inv.due_date, now) : 0;
        if (days <= 30) buckets.d0_30 += owed;
        else if (days <= 60) buckets.d31_60 += owed;
        else if (days <= 90) buckets.d61_90 += owed;
        else buckets.d91plus += owed;
        if (
          !oldest ||
          new Date(inv.due_date ?? inv.issue_date) < new Date(oldest.due_date ?? oldest.issue_date)
        ) {
          oldest = inv;
        }
      }
      const outstanding = buckets.d0_30 + buckets.d31_60 + buckets.d61_90 + buckets.d91plus;
      return { customer: c, outstanding, buckets, oldestInvoice: oldest };
    });
    return rows.sort((a, b) => b.outstanding - a.outstanding);
  }, [customers, invoices]);

  const totals = useMemo(() => {
    const t = { d0_30: 0, d31_60: 0, d61_90: 0, d91plus: 0, total: 0 };
    for (const r of aging) {
      t.d0_30 += r.buckets.d0_30;
      t.d31_60 += r.buckets.d31_60;
      t.d61_90 += r.buckets.d61_90;
      t.d91plus += r.buckets.d91plus;
      t.total += r.outstanding;
    }
    return t;
  }, [aging]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Users size={22} /> Customers
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          Add
        </Button>
      </div>

      <div className="mb-4 flex gap-2">
        {(['aging', 'list'] as const).map((tt) => (
          <button
            key={tt}
            onClick={() => setTab(tt)}
            className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
              tab === tt ? 'bg-ink text-paper border-ink' : 'border-line text-muted bg-white'
            }`}
          >
            {tt === 'aging' ? 'AR Aging' : 'All customers'}
          </button>
        ))}
      </div>

      {tab === 'aging' ? (
        <>
          {/* Aging summary */}
          <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            <BucketCard label="0–30 days" value={totals.d0_30} tone="green" />
            <BucketCard label="31–60 days" value={totals.d31_60} tone="blue" />
            <BucketCard label="61–90 days" value={totals.d61_90} tone="accent" />
            <BucketCard label="91+ days" value={totals.d91plus} tone="red" />
          </div>
          <Card className="bg-ink text-paper mb-4">
            <div className="flex items-center justify-between">
              <span className="text-paper-2">Total outstanding</span>
              <span className="tnum font-mono text-2xl font-semibold">
                {formatMoney(totals.total, regionCode)}
              </span>
            </div>
          </Card>

          {/* Aging rows */}
          {aging.filter((r) => r.outstanding > 0).length === 0 ? (
            <Card className="py-10 text-center">
              <p className="text-muted">All customers are paid up. 🎉</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {aging
                .filter((r) => r.outstanding > 0)
                .map((r) => (
                  <Card key={r.customer.id} compact>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{r.customer.name}</p>
                          {r.customer.on_hold && (
                            <Pill tone="red">
                              <Lock size={10} className="mr-1 inline" />
                              hold
                            </Pill>
                          )}
                        </div>
                        <p className="text-muted text-xs">
                          {r.customer.kind} · terms {r.customer.payment_terms_days}d · credit limit{' '}
                          {formatMoney(r.customer.credit_limit, regionCode)}
                        </p>
                        <div className="text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                          {r.buckets.d0_30 > 0 && (
                            <span>
                              0–30:{' '}
                              <span className="font-mono">
                                {formatMoney(r.buckets.d0_30, regionCode)}
                              </span>
                            </span>
                          )}
                          {r.buckets.d31_60 > 0 && (
                            <span className="text-blue">
                              31–60:{' '}
                              <span className="font-mono">
                                {formatMoney(r.buckets.d31_60, regionCode)}
                              </span>
                            </span>
                          )}
                          {r.buckets.d61_90 > 0 && (
                            <span className="text-accent">
                              61–90:{' '}
                              <span className="font-mono">
                                {formatMoney(r.buckets.d61_90, regionCode)}
                              </span>
                            </span>
                          )}
                          {r.buckets.d91plus > 0 && (
                            <span className="text-red">
                              91+:{' '}
                              <span className="font-mono">
                                {formatMoney(r.buckets.d91plus, regionCode)}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="tnum font-mono text-sm font-semibold">
                          {formatMoney(r.outstanding, regionCode)}
                        </p>
                        {r.buckets.d91plus > 0 && (
                          <Pill tone="red" className="mt-1">
                            <AlertTriangle size={10} className="mr-1 inline" />
                            cut off
                          </Pill>
                        )}
                        <button
                          onClick={() => setSelected(r.customer)}
                          className="text-accent mt-1 block w-full text-right text-[11px] underline"
                        >
                          {r.customer.on_hold ? 'Release' : 'Manage'}
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {customers.map((c) => (
            <Card key={c.id} compact className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{c.name}</p>
                  {c.on_hold && (
                    <Pill tone="red">
                      <Lock size={10} className="mr-1 inline" />
                      hold
                    </Pill>
                  )}
                </div>
                <p className="text-muted text-xs">
                  {c.kind} · limit {formatMoney(c.credit_limit, regionCode)} ·{' '}
                  {c.payment_terms_days} day terms
                </p>
              </div>
              <button
                onClick={() => setSelected(c)}
                className="bg-paper-2 hover:bg-line rounded-btn px-3 py-1.5 text-xs font-medium"
              >
                {c.on_hold ? 'Release' : 'Manage'}
              </button>
            </Card>
          ))}
        </div>
      )}

      {/* New customer modal */}
      <Modal open={open} onClose={() => setOpen(false)} title="Add customer">
        <div className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Email (optional)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Credit limit"
              type="number"
              numeric
              leftAddon="P"
              value={form.credit_limit || ''}
              onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })}
            />
            <Input
              label="Terms (days)"
              type="number"
              numeric
              value={form.terms || ''}
              onChange={(e) => setForm({ ...form, terms: Number(e.target.value) })}
            />
          </div>
          <Button full onClick={create}>
            Save
          </Button>
        </div>
      </Modal>

      {/* Manage / hold modal */}
      <Modal
        open={selected !== null}
        onClose={() => {
          setSelected(null);
          setHoldReason('');
        }}
        title={selected ? selected.name : ''}
      >
        {selected && (
          <div className="space-y-4">
            <div className="text-muted text-sm">
              {selected.kind} · credit limit {formatMoney(selected.credit_limit, regionCode)} ·{' '}
              {selected.payment_terms_days}-day terms
            </div>

            {/* Customer ledger */}
            <div>
              <h3 className="font-display mb-2 text-sm font-semibold">Customer ledger</h3>
              <CustomerLedger customerId={selected.id} invoices={invoices} />
            </div>

            {/* Hold controls */}
            <div className="border-line border-t pt-3">
              {selected.on_hold ? (
                <>
                  <div className="bg-red/10 text-red rounded-card mb-3 p-3 text-sm">
                    <p className="font-semibold">Currently on credit hold</p>
                    <p className="text-xs">{selected.hold_reason ?? 'No reason recorded'}</p>
                    <p className="mt-2 text-xs">
                      Invoicing this customer is blocked until you release the hold.
                    </p>
                  </div>
                  <Button
                    full
                    variant="secondary"
                    leftIcon={<Unlock size={16} />}
                    onClick={() => toggleHold(selected)}
                  >
                    Release credit hold
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-muted mb-2 text-sm">
                    Place this customer on credit hold to block new invoices. Use when invoices have
                    been overdue 60–90+ days.
                  </p>
                  <Input
                    label="Reason (shown in audit log)"
                    placeholder="90+ days overdue"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                  />
                  <Button
                    full
                    variant="danger"
                    className="mt-3"
                    leftIcon={<Lock size={16} />}
                    onClick={() => toggleHold(selected, holdReason || '90+ days overdue')}
                  >
                    Place on credit hold
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </main>
  );
}

function CustomerLedger({ customerId, invoices }: { customerId: string; invoices: Invoice[] }) {
  const regionCode = useRegionStore((s) => s.code);
  const my = invoices
    .filter((i) => i.customer_id === customerId)
    .sort((a, b) => b.issue_date.localeCompare(a.issue_date));

  if (my.length === 0) {
    return <p className="text-muted text-xs">No invoices on file.</p>;
  }

  const totalBilled = my.reduce((s, i) => s + i.total, 0);
  const totalPaid = my.reduce((s, i) => s + i.paid_amount, 0);
  const totalOwed = my.reduce(
    (s, i) => s + (i.status === 'cancelled' ? 0 : i.total - i.paid_amount),
    0,
  );

  return (
    <div className="space-y-1 text-xs">
      <div className="text-muted mb-2 grid grid-cols-3 gap-1">
        <div>
          <p>Billed</p>
          <p className="tnum text-ink font-mono text-sm">{formatMoney(totalBilled, regionCode)}</p>
        </div>
        <div>
          <p>Paid</p>
          <p className="tnum text-green font-mono text-sm">{formatMoney(totalPaid, regionCode)}</p>
        </div>
        <div>
          <p>Owing</p>
          <p className={`tnum font-mono text-sm ${totalOwed > 0 ? 'text-red' : 'text-ink'}`}>
            {formatMoney(totalOwed, regionCode)}
          </p>
        </div>
      </div>
      <div className="border-line max-h-48 overflow-auto rounded border">
        {my.map((inv) => {
          const owed = inv.total - inv.paid_amount;
          const isOpen = owed > 0 && inv.status !== 'cancelled';
          return (
            <div
              key={inv.id}
              className="border-line flex items-center justify-between border-b px-2 py-1.5 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <p className="font-mono">{inv.invoice_number}</p>
                <p className="text-muted">
                  Issued {inv.issue_date}
                  {inv.due_date && ` · due ${inv.due_date}`}
                </p>
              </div>
              <div className="text-right">
                <p className="tnum font-mono">{formatMoney(inv.total, regionCode)}</p>
                {isOpen ? (
                  <Pill tone="red">{formatMoney(owed, regionCode)} owing</Pill>
                ) : inv.status === 'paid' ? (
                  <Pill tone="green">paid</Pill>
                ) : (
                  <Pill tone="neutral">{inv.status}</Pill>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BucketCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'accent' | 'red';
}) {
  const regionCode = useRegionStore((s) => s.code);
  const colour: Record<string, string> = {
    green: 'text-green',
    blue: 'text-blue',
    accent: 'text-accent',
    red: 'text-red',
  };
  return (
    <Card compact>
      <p className="text-muted text-xs">{label}</p>
      <p className={`tnum font-mono text-sm ${colour[tone]}`}>{formatMoney(value, regionCode)}</p>
    </Card>
  );
}
